/**
 * PWA Install Prompt — Dismissible banner
 */

import { useState, useEffect } from "react";
import {
    getInstallPrompt,
    onInstallPromptChange,
    clearInstallPrompt,
    isIos,
    isInStandaloneMode,
    type BeforeInstallPromptEvent,
} from "../services/pwaInstall";
import { brand } from "@brand";

const DISMISS_KEY = "bb-pwa-prompt-dismissed";

export function InstallPrompt() {
    const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
    const [showIosPrompt, setShowIosPrompt] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (isInStandaloneMode()) return;
        if (localStorage.getItem(DISMISS_KEY)) return;

        // Only show the install banner on mobile devices
        const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent)
            || (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
        if (!isMobile) return;

        if (isIos()) {
            setShowIosPrompt(true);
        }

        // Set initial value if event was already captured
        setInstallEvent(getInstallPrompt());
        return onInstallPromptChange((e) => setInstallEvent(e));
    }, []);

    const handleDismiss = () => {
        localStorage.setItem(DISMISS_KEY, "true");
        setDismissed(true);
        setShowIosPrompt(false);
        setInstallEvent(null);
    };

    const handleInstall = async () => {
        if (!installEvent) return;
        await installEvent.prompt();
        const { outcome } = await installEvent.userChoice;
        if (outcome === "accepted") clearInstallPrompt();
    };

    if (dismissed) return null;

    if (installEvent) {
        return (
            <div style={bannerStyle}>
                <span style={textStyle}>Add {brand.name} to your home screen for quick access</span>
                <div style={btnRow}>
                    <button style={primaryBtn} onClick={handleInstall}>Install</button>
                    <button style={secondaryBtn} onClick={handleDismiss}>Not now</button>
                </div>
            </div>
        );
    }

    if (showIosPrompt) {
        return (
            <div style={bannerStyle}>
                <span style={textStyle}>
                    To install: tap <strong>Share</strong> <span style={{ fontSize: 18 }}>⎋</span> then <strong>Add to Home Screen</strong>
                </span>
                <button style={secondaryBtn} onClick={handleDismiss}>Got it</button>
            </div>
        );
    }

    return null;
}

const bannerStyle: React.CSSProperties = {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 16px",
    background: brand.colors.installPromptBackground,
    color: "#fff",
    fontFamily: brand.splash.fontFamily,
    fontSize: 14,
    boxShadow: "0 -2px 12px rgba(0,0,0,0.15)",
};

const textStyle: React.CSSProperties = { flex: 1 };
const btnRow: React.CSSProperties = { display: "flex", gap: 8, flexShrink: 0 };

const primaryBtn: React.CSSProperties = {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    background: brand.colors.installPromptAccent,
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
};

const secondaryBtn: React.CSSProperties = {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.2)",
    background: "transparent",
    color: "rgba(255,255,255,0.8)",
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
};
